import { useState, Suspense } from "react";
import { useTranslation } from "react-i18next";

import type { ActiveWidget } from "@/pages/EventPage/hooks/useEventWidgets";

import { useGetProfile } from "@/api/bale";
import { Card, CardSubtitleHeader } from "@/components/Card";
import { uuidVersion } from "@/fp/uuidVersion";
import { useCurrentEventSuspense } from "@/pages/EventPage/hooks/useCurrentEvent";

import { EditingDirectionsCard } from "./EditingDirectionsCard";
import { StaticDirectionsCard } from "./StaticDirectionsCard";

export type DirectionsWidget = Extract<ActiveWidget, { widget_type: "directions" }>;

export function DirectionsCard({ widget }: { widget: DirectionsWidget }) {
  const { t } = useTranslation();

  return (
    <Suspense
      fallback={
        <Card>
          <CardSubtitleHeader>{t("event-directions")}</CardSubtitleHeader>
        </Card>
      }
    >
      <EditableDirectionsCardContent widget={widget} />
    </Suspense>
  );
}

function EditableDirectionsCardContent({ widget }: { widget: DirectionsWidget }) {
  const me = useGetProfile("@me", { query: { select: (data) => data.data.profile } });
  const event = useCurrentEventSuspense({ query: { select: (data) => data.data } });

  const isTransient = uuidVersion(widget.id) === 4;
  const [isEditing, setIsEditing] = useState(isTransient);
  const isHost = me.isSuccess && event.data.event.account_id === me.data.id;

  if (isEditing) {
    return (
      <EditingDirectionsCard
        widget={widget}
        onSave={() => setIsEditing(false)}
        onCancel={isTransient ? undefined : () => setIsEditing(false)}
      />
    );
  }

  return <StaticDirectionsCard widget={widget} onEdit={isHost ? () => setIsEditing(true) : undefined} />;
}
